// Work kit — issue COMMENT COMPOSER controller (wave 4, Track E). Mounted on
// the comments tab; owns the pinned semantics:
//
//   - draft autosave per issue id (localStorage, per-viewer convenience only)
//     — restored on connect, cleared when the comment is accepted (pin E4),
//   - Ctrl/Cmd+Enter submits; plain Enter keeps its native newline (the
//     text editor is never reached into — see lib/text_editor),
//   - submit → optimistic comment PREPENDED from the rendered template
//     (textContent only — never HTML inserted from user input) → emit
//     ui--work:comment-create (pin E5). Every submit mints a per-op
//     `op_token` (the comments W9 discipline): stamped on the optimistic row
//     (`data-work-comment-op`) and carried on the event detail, so the host's
//     echoed ack reverts exactly one op instead of sweeping every concurrent
//     optimistic submit,
//   - comment-ack ok → toasts cleared, the optimistic row STANDS IN until
//     the host re-render swaps the list for the persisted comments (the kit
//     never re-renders — removing it early would flash the accepted comment
//     away, and an ack-only glue island would lose it); !ok → the FAILED
//     op's row removed, THAT op's body restored as the draft (the op
//     registry, not `_lastSubmittedBody` — a second consecutive failure must
//     not wipe the user's text), exactly one toast (pin E6); a tokenless
//     (legacy W4) ack cannot be attributed to an op and keeps the coarse
//     sweep.
//
// The kit performs no HTTP; events bubble from the issue body root.

import { Controller } from "@hotwired/stimulus"
import { clearToasts, emitFromIssueRoot, showToast, toastRegionFor } from "../../../lib/work/mutation_seam"
import { clearEditor, editorText, setEditorText } from "../../../lib/text_editor"

const COMMENT_ACK_EVENT = "ui--work:comment-ack"
const CREATE_EVENT = "ui--work:comment-create"
const DRAFT_KEY_PREFIX = "conek-ui:work:comment-draft:"

// Per-op token generator — the comments W9 idiom (module-local counter +
// crypto UUID, Math.random fallback), scoped to this controller.
let opCounter = 0
function newOpToken() {
  opCounter += 1
  if (globalThis.crypto?.randomUUID) {
    return `work-issue-comment-${opCounter}-${globalThis.crypto.randomUUID()}`
  }
  return `work-issue-comment-${opCounter}-${Math.random().toString(36).slice(2)}`
}

// The op registry is FIFO-capped (the comments W9 rationale): tokenless
// legacy acks never delete their entry, so an unbounded map would grow with
// every submit on a long-lived page.
const OP_CAP = 20

export default class extends Controller {
  static values = {
    issueId: String,
    canComment: { type: Boolean, default: false },
    viewerName: { type: String, default: "" },
    errorMessage: { type: String, default: "" },
  }

  static targets = ["commentList", "commentTemplate", "live"]

  connect() {
    this._teardown()
    this._onAck = this._handleAck.bind(this)
    document.addEventListener(COMMENT_ACK_EVENT, this._onAck)
    // The op registry is connect-once state, not per-connect state: a
    // same-instance re-bind (Turbo cache restore racing a morph) must keep
    // the in-flight ops' bodies — the failure path restores the draft from
    // here, and a reset map would write an EMPTY draft over the user's text.
    if (!this._opBodies) this._opBodies = new Map()
    // Deferred to a microtask: Stimulus connects a parent before its children,
    // so the composer's own ui--text-editor controller does not exist yet at
    // this point and the restore would find nothing to write into. Stimulus
    // connects a whole batch of elements synchronously, so the child is up by
    // the time this runs.
    if (this.canCommentValue) queueMicrotask(() => this._restoreDraft())
  }

  disconnect() {
    this._teardown()
  }

  // Teardown-first re-bind (dashboard_grid idiom, ghost-closure family): a
  // same-instance double-connect (Turbo cache restore racing a morph) must
  // not stack a second document ack closure — the orphaned ghost re-handles
  // every ack (double toasts, double row sweeps). Removing first is a no-op
  // when nothing is bound.
  _teardown() {
    document.removeEventListener(COMMENT_ACK_EVENT, this._onAck)
    this._onAck = null
  }

  // Editor input (ui--text-editor:change, re-dispatched by the component from
  // whichever engine it runs) → autosave.
  draftChanged() {
    this._saveDraft(this._editorText())
  }

  keydown(event) {
    if (event.key !== "Enter") return
    if (!(event.metaKey || event.ctrlKey)) return
    event.preventDefault()
    this.submit()
  }

  submit() {
    if (!this.canCommentValue) return
    const body = this._editorText().trim()
    if (!body) return

    const opToken = newOpToken()
    this._rememberOp(opToken, body)
    this._prependOptimistic(body, opToken)
    this._clearEditor()
    this._saveDraft("") // accepted path clears; failures restore below
    this._announce("")

    emitFromIssueRoot(this.element, CREATE_EVENT, {
      issue_id: this.issueIdValue,
      body: body,
      op_token: opToken,
    })
  }

  // --- internals ---

  // Optimistic row: cloned from the ERB template (kit markup, safe), filled
  // with textContent only — user input is NEVER inserted as HTML. `opToken`
  // stamps the row so the failure path can revert exactly this op (W9).
  _prependOptimistic(body, opToken) {
    if (!this.hasCommentTemplateTarget || !this.hasCommentListTarget) return
    const node = this.commentTemplateTarget.content.firstElementChild
    if (!node) return
    const clone = node.cloneNode(true)
    clone.setAttribute("data-work-comment-optimistic", "")
    clone.setAttribute("data-work-comment-op", opToken)
    const part = (name) => clone.querySelector(`[data-work-comment-part="${name}"]`)
    const timestamp = part("timestamp")
    if (timestamp) timestamp.textContent = this._nowLabel()
    const actor = part("actor")
    if (actor) actor.textContent = this.viewerNameValue
    const bodyPart = part("body")
    if (bodyPart) bodyPart.textContent = body
    this.commentListTarget.prepend(clone)
  }

  _handleAck(event) {
    const detail = event.detail || {}
    if (detail.issue_id !== this.issueIdValue) return
    const region = toastRegionFor(this.element)
    // The host echoes the submit's per-op `op_token` back on the ack (it rides
    // the event detail verbatim).
    const token = typeof detail.op_token === "string" && detail.op_token ? detail.op_token : null

    if (detail.ok) {
      // Accepted: the optimistic row stands in until the host re-render
      // replaces the list with the persisted comments. Removing it here would
      // flash the accepted comment away between ack and re-render — and a
      // glue island that acks without re-rendering would lose it entirely
      // (spec E.3/E5 realigned: only the failure path removes the row).
      if (token) this._opBodies.delete(token)
      clearToasts(region)
      this._announce("done")
    } else {
      // Failed: the FAILED op's row leaves and ITS OWN body survives as the
      // draft so the user does not retype (the op registry — a newer submit's
      // body is not this op's, and a second consecutive failure must not wipe
      // the persisted draft). The echoed token scopes the revert to exactly
      // one row — concurrent optimistic submits for this issue stand. A
      // tokenless (legacy W4) ack cannot be attributed to an op, so it keeps
      // the coarse sweep. The restore rides the REGISTRY, not the row: a
      // host re-render can drop optimistic rows between submit and the
      // failing ack — the row's absence removes nothing and the body still
      // comes back.
      if (token) {
        const row = this._optimisticRowByOp(token)
        if (row) row.remove()
        const body = this._opBodies.get(token)
        if (body !== undefined) {
          this._saveDraft(body)
          this._opBodies.delete(token)
        } else if (this._lastSubmittedBody) {
          // Registry miss (a double-ack for one token, an op evicted by the
          // cap): the last known body beats blanking the draft — and with
          // none known, leave the persisted draft alone.
          this._saveDraft(this._lastSubmittedBody)
        }
      } else {
        this.element.querySelectorAll("[data-work-comment-optimistic]").forEach((node) => node.remove())
        this._saveDraft(this._lastSubmittedBody || "")
      }
      showToast(region, this.errorMessageValue, "error")
      this._announce(this.errorMessageValue)
    }
    this._lastSubmittedBody = null
  }

  _optimisticRows() {
    return Array.from(this.element.querySelectorAll("[data-work-comment-optimistic]"))
  }

  // The optimistic row minted by one submit, found by iteration over the
  // stamped rows (the kit's lookup idiom — never an interpolated selector).
  _optimisticRowByOp(token) {
    return this._optimisticRows().find((node) => node.getAttribute("data-work-comment-op") === token) || null
  }

  // Registry the failed-ack draft restore reads from: the op's OWN body, not
  // `_lastSubmittedBody` (which is whatever was submitted last — wrong for a
  // non-latest op failing while a newer one is still in flight).
  _rememberOp(token, body) {
    if (this._opBodies.size >= OP_CAP) {
      this._opBodies.delete(this._opBodies.keys().next().value)
    }
    this._opBodies.set(token, body)
  }

  // --- editor access (through Ui::TextEditorComponent's own controller) ---

  _editorText() {
    return editorText(this.application, this.element)
  }

  _clearEditor() {
    this._lastSubmittedBody = this._editorText().trim()
    clearEditor(this.application, this.element)
  }

  // --- draft persistence (per-viewer convenience; localStorage only) ---

  _draftKey() {
    return `${DRAFT_KEY_PREFIX}${this.issueIdValue}`
  }

  _saveDraft(text) {
    try {
      if (text) {
        window.localStorage.setItem(this._draftKey(), text)
      } else {
        window.localStorage.removeItem(this._draftKey())
      }
    } catch (_error) {
      // Storage may be unavailable (private window, cleared site data) — the
      // draft simply does not persist; never break composing.
    }
  }

  _restoreDraft() {
    let draft = ""
    try {
      draft = window.localStorage.getItem(this._draftKey()) || ""
    } catch (_error) {
      return
    }
    if (!draft) return
    // Never clobber what is already on screen: a restore only ever fills an
    // EMPTY composer.
    if (this._editorText().trim()) return
    setEditorText(this.application, this.element, draft)
  }

  _nowLabel() {
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date())
    } catch (_error) {
      return ""
    }
  }

  _announce(text) {
    if (!this.hasLiveTarget) return
    this.liveTarget.textContent = text
  }
}
